import React, { useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * Company main page component
 * Serves as the landing page for the Company section
 * 
 * @returns {JSX.Element} The rendered Company page
 */
const CompanyPage: React.FC = () => {
  try {
    // Log component rendering
    console.log('Rendering Company page');

    // Company sections with descriptions
    const companySections = [
      {
        title: 'About Us',
        description: 'Learn about our mission, values, and the team behind Raidu.',
        link: '/company/about',
        ctaText: 'Our Story',
      },
      {
        title: 'Careers',
        description: 'Join our growing team and help shape the future of AI security.',
        link: '/company/careers',
        ctaText: 'View Openings',
      },
      {
        title: 'Press',
        description: 'Latest news, media coverage, and press releases about Raidu.',
        link: '/company/press',
        ctaText: 'Latest News',
      },
      {
        title: 'Contact',
        description: 'Get in touch with our team for inquiries, support, or partnerships.',
        link: '/company/contact',
        ctaText: 'Contact Us',
      },
    ];

    // Company timeline
    const timeline = [
      {
        name: 'Founded Raidu',
        description: 'Raidu was founded by a team of AI researchers and cybersecurity experts who recognized a critical gap in the enterprise AI landscape.',
        date: 'Jan 2023',
        dateTime: '2023-01',
      },
      {
        name: 'Secured $12M in funding',
        description: 'Raised seed funding from leading venture capital firms specializing in AI and enterprise security.',
        date: 'May 2023',
        dateTime: '2023-05',
      },
      {
        name: 'Released beta platform',
        description: 'Launched our private beta with select enterprise customers across healthcare, finance, and government sectors.',
        date: 'Oct 2023',
        dateTime: '2023-10',
      },
      {
        name: 'Global launch of Raidu platform',
        description: 'Released our enterprise-grade AI control plane to organizations worldwide, with comprehensive security and compliance features.',
        date: 'Mar 2024',
        dateTime: '2024-03',
      },
    ];

    // Customer logos
    const customers = [
      { name: 'Enterprise 1', logo: '/logos/placeholder-logo-1.svg' },
      { name: 'Enterprise 2', logo: '/logos/placeholder-logo-2.svg' },
      { name: 'Enterprise 3', logo: '/logos/placeholder-logo-3.svg' },
      { name: 'Enterprise 4', logo: '/logos/placeholder-logo-4.svg' },
      { name: 'Enterprise 5', logo: '/logos/placeholder-logo-5.svg' },
    ];

    return (
      <div className="bg-white">
        {/* Hero section */}
        <div className="relative isolate overflow-hidden bg-gradient-to-b from-indigo-100/20 to-white pt-14">
          <div
            aria-hidden="true"
            className="absolute inset-y-0 right-1/2 -z-10 -mr-96 w-[200%] origin-top-right skew-x-[-30deg] bg-white shadow-xl shadow-indigo-600/10 ring-1 ring-indigo-50 sm:-mr-80 lg:-mr-96"
          />
          <div className="mx-auto max-w-7xl px-6 py-32 sm:py-40 lg:px-8">
            <div className="mx-auto max-w-2xl lg:mx-0 lg:grid lg:max-w-none lg:grid-cols-2 lg:gap-x-16 lg:gap-y-8 xl:grid-cols-1 xl:grid-rows-1 xl:gap-x-8">
              <h1 className="max-w-2xl text-5xl font-semibold tracking-tight text-gray-900 sm:text-6xl lg:col-span-2 xl:col-auto">
                We're building the future of enterprise AI governance
              </h1>
              <div className="mt-6 max-w-xl lg:mt-0 xl:col-end-1 xl:row-start-1">
                <p className="text-lg text-gray-600 sm:text-xl/8">
                  Raidu is on a mission to build the most secure and reliable AI control plane for enterprises.
                  Our platform helps organizations govern, secure, and scale AI across teams, tools, and workflows.
                </p>
                <div className="mt-10 flex items-center gap-x-6">
                  <Link
                    to="/company/about"
                    className="rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 px-5 py-3 text-sm font-semibold text-white shadow-md hover:from-indigo-500 hover:to-purple-500 transition-all duration-300 transform hover:scale-105 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                    onClick={() => {
                      try {
                        // Log navigation event
                        console.log('User navigated to About page from Company hero');
                      } catch (error) {
                        // Log any errors during click handling
                        console.error('Error handling navigation to About page:', error);
                      }
                    }}
                  >
                    Learn about us
                  </Link>
                  <Link
                    to="/company/contact"
                    className="text-sm font-semibold leading-6 text-indigo-600 hover:text-indigo-500"
                    onClick={() => {
                      try {
                        // Log navigation event
                        console.log('User navigated to Contact page from Company hero');
                      } catch (error) {
                        // Log any errors during click handling
                        console.error('Error handling navigation to Contact page:', error);
                      }
                    }}
                  >
                    Contact us <span aria-hidden="true">→</span>
                  </Link>
                </div>
              </div>
              <img
                alt="Raidu team collaboration"
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1280&q=80"
                className="mt-10 aspect-6/5 w-full max-w-lg rounded-2xl object-cover sm:mt-16 lg:mt-0 lg:max-w-none xl:row-span-2 xl:row-end-2 xl:mt-36"
                onError={(e) => {
                  try {
                    // Log error loading image
                    console.error('Error loading hero image', e.currentTarget.src);
                    // Set fallback image
                    e.currentTarget.src = "https://tailwindcss.com/plus-assets/img/logos/tuple-logo-gray-900.svg";
                  } catch (error) {
                    // Log any errors during error handling
                    console.error('Error in image error handling:', error);
                  }
                }}
              />
            </div>
          </div>
          <div className="absolute inset-x-0 bottom-0 -z-10 h-24 bg-gradient-to-t from-white sm:h-32" />
        </div>

        {/* Timeline section */}
        <div className="mx-auto -mt-8 max-w-7xl px-6 lg:px-8">
          <div className="mx-auto grid max-w-2xl grid-cols-1 gap-8 overflow-hidden lg:mx-0 lg:max-w-none lg:grid-cols-4">
            {timeline.map((item) => (
              <div key={item.name}>
                <time dateTime={item.dateTime} className="flex items-center text-sm/6 font-semibold text-indigo-600">
                  <svg viewBox="0 0 4 4" aria-hidden="true" className="mr-4 size-1 flex-none">
                    <circle r={2} cx={2} cy={2} fill="currentColor" />
                  </svg>
                  {item.date}
                  <div
                    aria-hidden="true"
                    className="absolute -ml-2 h-px w-screen -translate-x-full bg-gray-900/10 sm:-ml-4 lg:static lg:-mr-6 lg:ml-8 lg:w-auto lg:flex-auto lg:translate-x-0"
                  />
                </time>
                <p className="mt-6 text-lg/8 font-semibold tracking-tight text-gray-900">{item.name}</p>
                <p className="mt-1 text-base/7 text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Company sections */}
        <div className="mx-auto mt-32 max-w-7xl px-6 sm:mt-40 lg:px-8">
          <div className="mx-auto max-w-2xl lg:mx-0">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">Explore Raidu</h2>
            <p className="mt-6 text-lg leading-8 text-gray-600">
              Learn more about our company, our mission, and how we're helping enterprises adopt AI with confidence.
            </p>
          </div>
          
          <div className="mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-x-8 gap-y-12 sm:mt-20 lg:mx-0 lg:max-w-none lg:grid-cols-2">
            {companySections.map((section) => (
              <div 
                key={section.title} 
                className="flex flex-col bg-white p-8 shadow-sm rounded-xl border border-gray-100 hover:shadow-md transition-shadow"
              >
                <h2 className="text-2xl font-bold text-gray-900 mb-4">{section.title}</h2>
                <p className="text-gray-600 flex-grow mb-6">{section.description}</p>
                <div>
                  <Link
                    to={section.link}
                    className="text-indigo-600 font-medium flex items-center hover:text-indigo-500"
                    onClick={() => {
                      try {
                        // Log navigation event
                        console.log(`User navigated to ${section.link}`);
                      } catch (error) {
                        // Log any errors during click handling
                        console.error(`Error handling navigation to ${section.link}:`, error);
                      }
                    }}
                  >
                    {section.ctaText}
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-1" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Logo cloud */}
        <div className="mx-auto mt-32 max-w-7xl sm:mt-40 sm:px-6 lg:px-8">
          <div className="relative isolate overflow-hidden bg-gray-900 px-6 py-24 text-center shadow-2xl sm:rounded-3xl sm:px-16">
            <h2 className="mx-auto max-w-2xl text-3xl font-bold tracking-tight text-white sm:text-4xl">
              Trusted by leading enterprises
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-lg leading-8 text-gray-300">
              Organizations across healthcare, finance, legal, and government sectors trust Raidu to secure their AI operations and ensure compliance.
            </p>
            <div className="mx-auto mt-20 grid max-w-lg grid-cols-4 items-center gap-x-8 gap-y-12 sm:max-w-xl sm:grid-cols-6 sm:gap-x-10 sm:gap-y-14 lg:max-w-4xl lg:grid-cols-5">
              {customers.map((customer, index) => (
                <img
                  key={index}
                  alt={customer.name}
                  src={customer.logo}
                  className="col-span-2 max-h-12 w-full object-contain lg:col-span-1"
                  onError={(e) => {
                    try {
                      // Log error loading image
                      console.error(`Error loading customer logo: ${customer.name}`, e.currentTarget.src);
                      // Set fallback image
                      e.currentTarget.src = "https://tailwindcss.com/plus-assets/img/logos/tuple-logo-white.svg";
                    } catch (error) {
                      // Log any errors during error handling
                      console.error('Error in image error handling:', error);
                    }
                  }}
                />
              ))}
            </div>
            <div aria-hidden="true" className="absolute -top-24 right-0 -z-10 transform-gpu blur-3xl">
              <div
                style={{
                  clipPath:
                    'polygon(73.6% 51.7%, 91.7% 11.8%, 100% 46.4%, 97.4% 82.2%, 92.5% 84.9%, 75.7% 64%, 55.3% 47.5%, 46.5% 49.4%, 45% 62.9%, 50.3% 87.2%, 21.3% 64.1%, 0.1% 100%, 5.4% 51.1%, 21.4% 63.9%, 58.9% 0.2%, 73.6% 51.7%)',
                }}
                className="aspect-[1404/767] w-[87.75rem] bg-gradient-to-r from-[#80caff] to-[#4f46e5] opacity-25"
              />
            </div>
          </div>
        </div>

        {/* CTA section */}
        <div className="mx-auto mt-32 max-w-7xl px-6 sm:mt-40 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">Ready to secure your AI?</h2>
            <p className="mt-6 text-lg leading-8 text-gray-600">
              Join the companies that trust Raidu to secure their AI operations and ensure compliance across their organization.
            </p>
            <div className="mt-10 flex items-center justify-center gap-x-6">
              <Link
                to="/company/contact"
                className="rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-3 text-base font-semibold text-white shadow-md hover:from-indigo-500 hover:to-purple-500 transition-all duration-300 transform hover:scale-105 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                onClick={() => {
                  try {
                    // Log navigation event
                    console.log('User navigated to Contact page from CTA');
                  } catch (error) {
                    // Log any errors during click handling
                    console.error('Error handling navigation to Contact page:', error);
                  }
                }}
              >
                Book a Demo
              </Link>
              <Link
                to="/company/about"
                className="text-base font-semibold leading-6 text-indigo-600 hover:text-indigo-500"
                onClick={() => {
                  try {
                    // Log navigation event
                    console.log('User navigated to About page from CTA');
                  } catch (error) {
                    // Log any errors during click handling
                    console.error('Error handling navigation to About page:', error);
                  }
                }}
              >
                Learn more <span aria-hidden="true">→</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  } catch (error) {
    // Log any errors during rendering
    console.error('Error rendering Company page:', error);
    
    // Return error state
    return (
      <div className="py-12 px-6 bg-red-50 rounded-lg mx-auto max-w-7xl">
        <h2 className="text-2xl font-bold text-red-700 mb-4">Error Loading Company Page</h2>
        <p className="text-red-600">
          We encountered an issue while loading this page. Please try refreshing or contact support.
        </p>
      </div>
    );
  }
};

export default CompanyPage;
